/* HolyShape wishlist: product handles kept in localStorage, heart buttons, count bubbles,
   the wishlist page list and the wishlist drawer. Original code for the own theme.
   Public API: window.HSWishlist.{get, has, add, remove, toggle, count}
   Event: document 'hs:wishlist:change' { detail: { handles, count } }
   Hooks for other areas: [data-hs-wishlist-count] shows the count, [data-hs-wishlist-open] opens the drawer. */
(() => {
  if (window.HSWishlist) return;
  const KEY = 'hs-wishlist';
  const OLD_KEY = 'dbtfy-wishlist'; // list kept by the previous theme (product URLs)

  const read = (k) => {
    try {
      return JSON.parse(localStorage.getItem(k) || 'null');
    } catch (e) {
      return null;
    }
  };
  const write = (list) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(list));
    } catch (e) {}
  };

  let handles = read(KEY);
  if (!Array.isArray(handles)) {
    handles = [];
    const old = read(OLD_KEY);
    if (Array.isArray(old)) {
      old.forEach((u) => {
        const m = String(u).match(/\/products\/([^/?#]+)/);
        if (m) handles.push(decodeURIComponent(m[1]));
      });
    }
    handles = [...new Set(handles)];
    write(handles);
  }

  const emit = () => {
    document.dispatchEvent(new CustomEvent('hs:wishlist:change', { detail: { handles: handles.slice(), count: handles.length } }));
    document.querySelectorAll('[data-hs-wishlist-count]').forEach((el) => {
      el.textContent = handles.length;
      el.dataset.count = handles.length;
    });
  };

  const api = {
    get: () => handles.slice(),
    has: (h) => handles.includes(h),
    count: () => handles.length,
    add(h) {
      if (!h || handles.includes(h)) return;
      handles.unshift(h);
      write(handles);
      emit();
    },
    remove(h) {
      handles = handles.filter((x) => x !== h);
      write(handles);
      emit();
    },
    toggle(h) {
      api.has(h) ? api.remove(h) : api.add(h);
      return api.has(h);
    },
  };
  window.HSWishlist = api;

  window.addEventListener('storage', (e) => {
    if (e.key !== KEY) return;
    handles = read(KEY) || [];
    emit();
  });

  class HsWishlistButton extends HTMLElement {
    connectedCallback() {
      this.handle = this.dataset.handle;
      this.button = this.querySelector('button');
      if (!this.button || !this.handle) return;
      this.sync = this.sync.bind(this);
      this.button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        api.toggle(this.handle);
      });
      document.addEventListener('hs:wishlist:change', this.sync);
      this.sync();
    }
    disconnectedCallback() {
      document.removeEventListener('hs:wishlist:change', this.sync);
    }
    sync() {
      const on = api.has(this.handle);
      this.classList.toggle('is-active', on);
      this.button.setAttribute('aria-pressed', on ? 'true' : 'false');
      const label = on ? this.dataset.labelRemove : this.dataset.labelAdd;
      if (label) this.button.setAttribute('aria-label', label);
    }
  }
  if (!customElements.get('hs-wishlist-button')) customElements.define('hs-wishlist-button', HsWishlistButton);

  /* list of saved products, rendered through the section rendering API of section `hs-wishlist-item` */
  class HsWishlistList extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-hs-wishlist-grid]');
      this.empty = this.querySelector('[data-hs-wishlist-empty]');
      this.cache = {};
      this.render = this.render.bind(this);
      document.addEventListener('hs:wishlist:change', this.render);
      this.render();
    }
    disconnectedCallback() {
      document.removeEventListener('hs:wishlist:change', this.render);
    }
    async fetchItem(h) {
      if (this.cache[h] !== undefined) return this.cache[h];
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      try {
        const res = await fetch(`${root}products/${encodeURIComponent(h)}?section_id=hs-wishlist-item`);
        if (!res.ok) throw new Error(res.status);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const item = doc.querySelector('[data-hs-wishlist-item]');
        this.cache[h] = item ? item.outerHTML : null;
      } catch (e) {
        this.cache[h] = null;
      }
      return this.cache[h];
    }
    async render() {
      const list = api.get();
      if (this.empty) this.empty.hidden = list.length > 0;
      if (!this.grid) return;
      this.grid.hidden = list.length === 0;
      const token = (this.token = Symbol());
      const parts = await Promise.all(list.map((h) => this.fetchItem(h)));
      if (token !== this.token) return;
      this.grid.innerHTML = parts.filter(Boolean).join('');
      list.forEach((h, i) => {
        if (parts[i] === null) api.remove(h); // product no longer exists
      });
    }
  }
  if (!customElements.get('hs-wishlist-list')) customElements.define('hs-wishlist-list', HsWishlistList);

  class HsWishlistDrawer extends HTMLElement {
    connectedCallback() {
      this.querySelectorAll('[data-hs-wishlist-close]').forEach((b) => b.addEventListener('click', () => this.close()));
      this.addEventListener('keyup', (e) => e.code === 'Escape' && this.close());
    }
    open(opener) {
      this.opener = opener;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
      const focusable = this.querySelector('[data-hs-wishlist-close]');
      focusable && focusable.focus();
    }
    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
      this.opener && this.opener.focus && this.opener.focus();
    }
  }
  if (!customElements.get('hs-wishlist-drawer')) customElements.define('hs-wishlist-drawer', HsWishlistDrawer);

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-hs-wishlist-open]');
    if (!opener) return;
    const drawer = document.querySelector('hs-wishlist-drawer');
    if (!drawer) return; // falls back to the link's href
    e.preventDefault();
    drawer.open(opener);
  });

  const ready = () => emit();
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', ready) : ready();
})();
